/**
 * Pre-insert bay-occupancy guard.
 *
 * Returns true when a vehicle is PHYSICALLY in the candidate bay right now
 * (Stage B/F.1: a current BAY location in `vehicle_location_current`). The
 * caller (OptiPlaceModal) uses this to defer a planned bay behind a car that
 * is really there.
 *
 * The legacy per-day `uniq_active_bay` index this used to mirror was retired
 * in 20261223100000: it let a PLAN (a specialty sub-ticket's `bay_id`) count
 * as occupancy. Physical exclusivity is `vlc_bay_exclusive` on current truth.
 *
 * Boundary: this only INSPECTS state. It does not write, schedule, or
 * assign — the caller decides how to react (typically: set bay_id =
 * NULL on the insert, keep scheduled_tech_id intact).
 */

// The guard needs the real client's `.from`. Matching the concrete client
// against a hand-rolled structural chain type expands the postgrest builder's
// generics and hits TS2589, so depend on the real client type directly (its
// concrete-literal query below type-checks the same way as everywhere else).
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

type SupabaseLike = Pick<SupabaseClient<Database>, 'from'>;

/**
 * Flat result shape. A boolean-discriminated union (`ok: true | false`) does
 * NOT narrow under this project's `strictNullChecks: false` config, so callers
 * couldn't reach `.error`/`.occupied` after an `if (!res.ok)` check. Optional
 * fields on a single shape read safely regardless of `ok` and need no
 * narrowing: `occupied` is set on success, `error` on failure.
 */
export interface BayOccupancyResult {
  ok: boolean;
  occupied?: boolean;
  error?: unknown;
}

export interface IsBayCurrentlyOccupiedArgs {
  supabase: SupabaseLike;
  bayId: string;
  scheduledDate: string;
  /**
   * The new ticket's id. Excluded from the query so that re-running
   * the guard for an in-flight insert (e.g., retry) doesn't false-
   * positive on the row this caller is about to write.
   */
  excludeTicketId?: string | null;
}

export async function isBayCurrentlyOccupied({
  supabase,
  bayId,
  scheduledDate,
  excludeTicketId,
}: IsBayCurrentlyOccupiedArgs): Promise<BayOccupancyResult> {
  // Stage B/F.1: physical occupancy is current physical truth — a live vehicle
  // whose current location is this bay — never a ticket's `bay_id` (which, on
  // a dependent ticket, is only a plan). Not day-scoped: a car left in the bay
  // yesterday still fills it today. `scheduledDate` is kept for callers.
  void scheduledDate;
  let q = supabase
    .from('vehicle_location_current')
    .select('ticket_id')
    .eq('bay_id', bayId)
    .eq('location_kind', 'BAY');
  if (excludeTicketId) {
    q = q.neq('ticket_id', excludeTicketId);
  }
  const { data, error } = await q.limit(1);
  if (error) {
    return { ok: false, error };
  }
  return { ok: true, occupied: Array.isArray(data) && data.length > 0 };
}
